import {
  COLOR_FLYWEIGHT,
  DEFAULT_CHART_COLORS,
  formatData,
  getGridColorsForCount,
} from '@utilities';

import { addSlideFooter } from '../slide_footer';
import { cleanQuestionTitle } from 'app/pages/Dashboard/pages/Survey/components/Dashboard/utilities/helpers.js';
import {
  FOOTER_TEXT,
  SURVEY_TITLE_OPTIONS,
  TABLE_BORDER,
  TABLE_FONT_SIZE,
  TEXT_COLOR,
  TITLE_TEXT,
} from '../constants';

/**
 * Transforms grid question data to chart format for stacked horizontal bar chart
 * @param {Object} questionData - Response data for grid/table question
 * @param {String} displayAs - Display format ('pct' or 'count')
 * @returns {Object}
 */
const getGridChartData = (questionData, displayAs = 'pct') => {
  if (questionData?.segments?.length) {
    return getGridChartDataFromSegments(questionData, displayAs);
  }
  const isCountDisplay = displayAs === 'count';
  const tableData = isCountDisplay ? questionData?.tableCount : questionData?.tablePct;

  if (tableData) {
    return getGridChartDataFromTable(tableData, displayAs);
  }
  return null;
};

// Helper function for segments-based data (original logic)
const getGridChartDataFromSegments = (questionData, displayAs = 'pct') => {
  const charts = [];
  const isCountDisplay = displayAs === 'count';
  const isPercentageDisplay = !isCountDisplay;

  questionData.segments.forEach((segment, index) => {
    const chartData = isCountDisplay ? segment.chartCount : segment.chartPct;

    if (!chartData?.series?.length || !chartData?.xAxis?.categories?.length) {
      return;
    }
    const categories = chartData.xAxis.categories;
    const series = chartData.series.map((seriesItem) => ({
      data: seriesItem.data || [],
      id: seriesItem.id,
      name: seriesItem.name,
    }));

    charts.push({
      categories,
      displayAs,
      format: chartData.format,
      isPercentageDisplay,
      segmentId: segment.id,
      series,
      title: segment.name || chartData.title,
    });
  });

  return charts;
};

// Helper function for table-based data
const getGridChartDataFromTable = (tableData, displayAs = 'pct') => {
  const isCountDisplay = displayAs === 'count';
  const isPercentageDisplay = !isCountDisplay;

  if (tableData.series && tableData.xAxis?.categories) {
    return [
      {
        categories: tableData.xAxis.categories,
        displayAs,
        isPercentageDisplay,
        series: tableData.series.map((s) => ({
          data: s.data || [],
          id: s.id,
          name: s.name,
        })),
        title: 'Grid Chart',
      },
    ];
  }

  return [
    {
      categories: ['No Data'],
      displayAs,
      isPercentageDisplay,
      series: [
        {
          data: [0],
          id: '1',
          name: 'Placeholder',
        },
      ],
      title: 'Grid Chart (No Data)',
    },
  ];
};

/**
 * Creates a single grid chart slide
 */
const createSingleGridSlide = ({
  chartData,
  compareBy,
  displayAs,
  pptx,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  const cleanTitle = cleanQuestionTitle(questionTitle);
  if (!cleanTitle) {
    return null;
  }

  const slide = pptx.addSlide();
  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
  const titleWithQuestionNumber = `${questionPrefix}${cleanTitle}`;

  const compareByLabel = compareBy?.label || compareBy?.value || compareBy;
  const slideTitle = compareByLabel
    ? `${titleWithQuestionNumber} Compared by: ${compareByLabel}`
    : titleWithQuestionNumber;

  const titleOptions = SURVEY_TITLE_OPTIONS;
  slide.addText(slideTitle, titleOptions);

  const chartYPosition = titleOptions.h > 1.0 ? 1.2 : 0.9;
  const chartHeight = titleOptions.h > 1.0 ? '70%' : '75%';
  const chartWidth = '91%';
  const chartXPosition = 0.25;

  try {
    const chartColors =
      getGridColorsForCount(chartData.series.length) ||
      DEFAULT_CHART_COLORS.slice(0, chartData.series.length);

    const isCountDisplay = displayAs === 'count';
    const isPercentageDisplay = chartData.isPercentageDisplay || !isCountDisplay;
    const axisFormatCode = isPercentageDisplay ? '0%' : '#,##0';
    const barGrouping = isPercentageDisplay ? 'percentStacked' : 'stacked';
    const axisMaxVal = isPercentageDisplay ? 1.0 : null;
    const axisMinVal = isPercentageDisplay ? 0.0 : null;
    const axisUnit = isPercentageDisplay ? 0.1 : 25;

    const chartOptions = {
      barDir: 'bar',
      barGrouping: barGrouping,
      catAxisLabelColor: TEXT_COLOR,
      catAxisLabelFontSize: 9,
      chartColors: chartColors,
      h: chartHeight,
      legendColor: TEXT_COLOR,
      legendFontSize: 10,
      legendPos: 'b',
      showDataLabels: true,
      showLegend: true,
      showTitle: false,
      valAxisLabelColor: TEXT_COLOR,
      valAxisLabelFontSize: 9,
      valAxisLabelFormatCode: axisFormatCode,
      w: chartWidth,
      x: chartXPosition,
      y: chartYPosition,
    };

    if (isPercentageDisplay && isFinite(axisMaxVal) && isFinite(axisMinVal) && isFinite(axisUnit)) {
      chartOptions.valAxisMajorUnit = axisUnit;
      chartOptions.valAxisMaxVal = axisMaxVal;
      chartOptions.valAxisMinVal = axisMinVal;
    }

    const reversedCategories = [...chartData.categories].reverse();
    const dataSeries = chartData.series.map((series) => ({
      labels: reversedCategories,
      name: series.name,
      values: [...(series.data || [])].reverse().map((value) => {
        if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
          return 0;
        }
        const numValue = parseFloat(value);
        if (isNaN(numValue) || !isFinite(numValue)) {
          return 0;
        }
        return Math.round(numValue * 1000) / 1000;
      }),
    }));

    slide.addChart(pptx.charts.BAR, dataSeries, chartOptions);
  } catch (error) {
    console.error('Error creating grid chart:', error);
    slide.addText('Grid chart could not be created', {
      color: TEXT_COLOR,
      fontSize: 12,
      h: 1,
      w: chartWidth,
      x: chartXPosition,
      y: chartYPosition,
    });
  }
  addSlideFooter({ pptx, reportType, reportUrl, slide });
  slide.addText(
    [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
    FOOTER_TEXT
  );
};

/**
 * Creates grid chart slides for TABLE type questions
 * @param {Object} options
 * @param {String} options.compareBy
 * @param {String} options.displayAs
 * @param {Object} options.pptx
 * @param {Object} options.questionData
 * @param {String} options.questionNumber
 * @param {String} options.questionTitle
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {String} options.title
 * @returns {Object}
 */
export const createGridChartSlide = ({
  compareBy,
  displayAs = 'pct',
  pptx,
  questionData,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  const charts = getGridChartData(questionData, displayAs);
  if (!charts || !charts.length) {
    return null;
  }

  for (const chart of charts) {
    // Use original question title with segment name if multiple charts, otherwise use original title
    const slideTitle =
      charts.length > 1 && chart.title !== questionTitle
        ? `${questionTitle} - ${chart.title}`
        : questionTitle;

    createSingleGridSlide({
      chartData: chart,
      compareBy,
      displayAs,
      pptx,
      questionNumber,
      questionTitle: slideTitle,
      reportType,
      reportUrl,
      title,
    });
  }
};

/**
 * Gets grid table data from question data
 * @param {Object} questionData - Response data for grid/table question
 * @param {String} displayAs - Display format ('pct' or 'count')
 * @returns {Object}
 */
const getGridTableData = (questionData, displayAs = 'pct') => {
  // For grid questions, table data is at the question level
  // Segments represent individual statements/rows, not demographic breakdowns
  const isCountDisplay = displayAs === 'count';
  const tableData = isCountDisplay ? questionData?.tableCount : questionData?.tablePct;
  return tableData;
};

/**
 * Groups table data by compare_by_breakout column
 * @param {Object} tableData
 * @returns {Object} - Grouped data keyed by breakout value
 */
const groupTableDataByBreakout = (tableData) => {
  if (!tableData?.data?.length) return {};

  const dataByGroup = {};

  tableData.data.forEach((row) => {
    const groupName = row.compare_by_breakout || 'All';
    if (!dataByGroup[groupName]) {
      dataByGroup[groupName] = [];
    }
    dataByGroup[groupName].push(row);
  });

  return dataByGroup;
};

/**
 * Creates a single grid table slide
 * @param {Object} options
 * @returns {Object}
 */
const createSingleGridTableSlide = ({
  pptx,
  reportType,
  reportUrl,
  slideTitle,
  tableData,
  title,
}) => {
  if (!tableData?.data?.length || !tableData?.columns?.length) {
    return null;
  }

  const slide = pptx.addSlide();

  slide.addText(slideTitle, {
    ...TITLE_TEXT,
    fontSize: 14,
  });

  const { columns, data } = tableData;
  const headers = columns.map((col) => col.Header || col.header || col.accessor);

  const tableRows = [
    headers.map((header) => ({
      options: {
        bold: true,
        color: TEXT_COLOR,
        fill: { color: COLOR_FLYWEIGHT },
        fontSize: TABLE_FONT_SIZE,
      },
      text: header,
    })),
    ...data.map((row) =>
      columns.map((col, colIndex) => {
        const accessor = col.accessor || col.accessorKey;
        const value = row[accessor];
        let formattedValue;

        if (col.format && value !== null && value !== undefined) {
          formattedValue = formatData({ format: col.format, value });
        } else {
          formattedValue = value?.toString() || '';
        }

        return {
          options: {
            align: colIndex === 0 ? 'left' : 'right',
            border: TABLE_BORDER,
            color: TEXT_COLOR,
            fontFace: 'Arial',
            fontSize: TABLE_FONT_SIZE,
            valign: 'middle',
          },
          text: formattedValue,
        };
      })
    ),
  ];

  const TOTAL_TABLE_WIDTH = 9.3;
  const columnCount = headers.length;

  let columnWidths;
  if (columnCount <= 1) {
    columnWidths = [TOTAL_TABLE_WIDTH];
  } else {
    const FIRST_COLUMN_RATIO = 0.25;
    const firstColumnWidth = TOTAL_TABLE_WIDTH * FIRST_COLUMN_RATIO;
    const remainingColumnWidth = (TOTAL_TABLE_WIDTH - firstColumnWidth) / (columnCount - 1);
    columnWidths = [firstColumnWidth, ...Array(columnCount - 1).fill(remainingColumnWidth)];
  }

  slide.addTable(tableRows, {
    border: TABLE_BORDER,
    colW: columnWidths,
    w: '95%',
    x: 0.3,
    y: 1.0,
  });

  addSlideFooter({ pptx, reportType, reportUrl, slide });
  slide.addText(
    [
      { text: 'Source: ' },
      { options: { hyperlink: { url: reportUrl } }, text: title || 'Grid Table' },
    ],
    FOOTER_TEXT
  );

  return slide;
};

/**
 * Creates grid table slides
 * @param {Object} options
 * @param {String} options.compareBy
 * @param {String} options.displayAs
 * @param {Object} options.pptx
 * @param {Object} options.questionData
 * @param {Number} options.questionNumber
 * @param {String} options.questionTitle
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {String} options.title
 * @returns {Object}
 */
export const createGridTableSlide = ({
  compareBy,
  displayAs = 'pct',
  pptx,
  questionData,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  if (!questionTitle || questionTitle.length < 3) {
    return null;
  }

  const cleanTitle = cleanQuestionTitle(questionTitle);
  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
  const titleWithQuestionNumber = `${questionPrefix}${cleanTitle}`;

  const compareByLabel = compareBy?.label || compareBy?.value || compareBy;
  const baseSlideTitle = compareByLabel
    ? `${titleWithQuestionNumber} Compared by: ${compareByLabel}`
    : titleWithQuestionNumber;

  const tableData = getGridTableData(questionData, displayAs);

  if (!tableData) {
    return null;
  }

  const dataByGroup = groupTableDataByBreakout(tableData);
  const groupNames = Object.keys(dataByGroup);

  if (groupNames.length > 1) {
    groupNames.forEach((groupName) => {
      const groupData = dataByGroup[groupName];
      const slideTitle = `${baseSlideTitle} - ${groupName} - Data Table`;

      const filteredColumns = tableData.columns.filter((col) => {
        const accessor = col.accessor || col.accessorKey;
        return accessor !== 'compare_by_breakout';
      });

      const groupTableData = {
        columns: filteredColumns,
        data: groupData,
      };

      const totalRows = groupData.length;

      if (totalRows > 10) {
        const rowsPerSlide = 10;
        const totalSlides = Math.ceil(totalRows / rowsPerSlide);

        for (let slideIndex = 0; slideIndex < totalSlides; slideIndex++) {
          const startIndex = slideIndex * rowsPerSlide;
          const endIndex = Math.min(startIndex + rowsPerSlide, totalRows);

          const slideTableData = {
            ...groupTableData,
            data: groupTableData.data.slice(startIndex, endIndex),
          };

          const paginatedSlideTitle =
            totalSlides > 1 ? `${slideTitle} (${slideIndex + 1} of ${totalSlides})` : slideTitle;

          createSingleGridTableSlide({
            pptx,
            reportType,
            reportUrl,
            slideTitle: paginatedSlideTitle,
            tableData: slideTableData,
            title,
          });
        }
      } else {
        createSingleGridTableSlide({
          pptx,
          reportType,
          reportUrl,
          slideTitle,
          tableData: groupTableData,
          title,
        });
      }
    });
  } else {
    const slideTitle = `${baseSlideTitle} - Data Table`;
    const totalRows = tableData.data?.length || 0;

    if (totalRows > 10) {
      const rowsPerSlide = 10;
      const totalSlides = Math.ceil(totalRows / rowsPerSlide);

      for (let slideIndex = 0; slideIndex < totalSlides; slideIndex++) {
        const startIndex = slideIndex * rowsPerSlide;
        const endIndex = Math.min(startIndex + rowsPerSlide, totalRows);

        const slideTableData = {
          ...tableData,
          data: tableData.data.slice(startIndex, endIndex),
        };

        const paginatedSlideTitle =
          totalSlides > 1 ? `${slideTitle} (${slideIndex + 1} of ${totalSlides})` : slideTitle;

        createSingleGridTableSlide({
          pptx,
          reportType,
          reportUrl,
          slideTitle: paginatedSlideTitle,
          tableData: slideTableData,
          title,
        });
      }
    } else {
      createSingleGridTableSlide({
        pptx,
        reportType,
        reportUrl,
        slideTitle,
        tableData: tableData,
        title,
      });
    }
  }

  return null;
};
